/**
 * SFX cue definitions. No audio assets exist yet, so every cue is a
 * synthesized tone/noise-burst spec rather than a file path — but the
 * shape is deliberately asset-shaped (id -> params) so swapping a cue to
 * a real sample later is a one-line change in AudioSystem.playSfx(), not
 * a rewrite of every call site.
 *
 * `type: 'noise'` renders as a filtered white-noise burst (impacts,
 * explosions); any other type is an oscillator waveform. `sweepTo`
 * glides the oscillator frequency over the cue's duration for a bit of
 * shape instead of a flat beep.
 *
 * `pitchJitter` randomly detunes each play by up to that fraction — for
 * cues that fire in rapid streams (weapon shots, hit ticks), so they read
 * as individual events instead of one looping sample. `minInterval`
 * (seconds) rate-limits a cue: extra triggers inside the window are
 * dropped (voice limiting), keeping a five-weapon build from becoming a
 * solid wall of clicks.
 */
export const SFX_CUES = {
  // --- player actions ---
  dash: { type: 'sawtooth', freq: 180, sweepTo: 760, duration: 0.16, volume: 0.22 },
  dashReady: { type: 'sine', freq: 880, duration: 0.06, volume: 0.1, minInterval: 0.2 },
  shoot: { type: 'square', freq: 620, sweepTo: 240, duration: 0.05, volume: 0.06, pitchJitter: 0.08, minInterval: 0.05 },
  dodge: { type: 'sine', freq: 720, sweepTo: 1100, duration: 0.1, volume: 0.13 },
  // Airy, near-subliminal tick — grazes can land several times a second in
  // dense patterns, so this leans on minInterval harder than anything else.
  graze: { type: 'sine', freq: 1500, sweepTo: 1800, duration: 0.04, volume: 0.05, pitchJitter: 0.15, minInterval: 0.08 },
  heal: { type: 'sine', freq: 500, sweepTo: 780, duration: 0.15, volume: 0.12, minInterval: 0.3 },
  // XP mote pickup (BULLET-HEAVEN.md §H1). The most-triggered cue in the game
  // — a wave stage collects hundreds — so it leans on minInterval and pitch
  // jitter harder than anything except `graze`, and sits at a volume where a
  // continuous stream reads as texture rather than as a machine gun. The
  // AudioSystem binding walks it up a short ladder on consecutive pickups so a
  // vacuum arrives as a rising run instead of one flat repeated tick.
  mote: { type: 'triangle', freq: 900, sweepTo: 1200, duration: 0.035, volume: 0.045, pitchJitter: 0.06, minInterval: 0.045 },
  revive: { type: 'sine', freq: 330, sweepTo: 990, duration: 0.6, volume: 0.3 },
  // --- Spoils chest (BULLET-HEAVEN.md §H3) ---
  // One card landing on the reel. Short and dry on purpose — this is the
  // mechanical click of a reel stop, and the CALLER walks it up a pitch ladder
  // (see SpoilsChest._startReel), so the cue itself must not carry a sweep
  // that fights the ladder. No minInterval: the reel already spaces these,
  // and dropping one would silently desync the run.
  spoilsTick: { type: 'square', freq: 520, duration: 0.06, volume: 0.16 },
  // The lid coming off, fired once as the burst ring goes out and before the
  // first card lands. A rising sweep so it reads as an opening rather than an
  // impact — `spoilsDrop` already owns the downward thunk, and having both
  // sweep the same way made the chest sound like it landed twice.
  spoilsOpen: { type: 'sawtooth', freq: 160, sweepTo: 620, duration: 0.26, volume: 0.24 },
  // The chest hitting the floor off a boss kill. Low and short — a weight
  // landing, not a fanfare; the fanfare is the reel that follows once the
  // player actually walks over it, and this cue's whole job is to make them
  // look at the corpse.
  spoilsDrop: { type: 'triangle', freq: 190, sweepTo: 90, duration: 0.22, volume: 0.22 },
  // The reel coming to rest. The only cue in the file whose job is to say
  // "that's all of it" — a long fall, so a one-card chest and a five-card
  // chest both end on the same full stop.
  spoilsSettle: { type: 'triangle', freq: 780, sweepTo: 260, duration: 0.45, volume: 0.2 },

  // Harmonics fusion — a rising sweep a notch below the ultimate cue.
  fused: { type: 'sawtooth', freq: 220, sweepTo: 880, duration: 0.4, volume: 0.24 },
  // A Harmonic coming into reach (BULLET-HEAVEN.md §H4): the same rising
  // interval as `fused` an octave up, on a soft triangle at half the volume.
  // It has to be recognizable as the fusion cue's opening without ever being
  // mistaken for the fusion itself landing.
  fusionReady: { type: 'triangle', freq: 440, sweepTo: 1760, duration: 0.3, volume: 0.13 },

  // --- champion ultimates (data/ultimates.js) ---
  // Charged and waiting: a clean two-note-feeling chime, well clear of the
  // level-up and fusion cues so they never get confused.
  ultimateReady: { type: 'triangle', freq: 780, sweepTo: 1170, duration: 0.22, volume: 0.22 },
  // Firing one — the loudest player-initiated cue in the game, deliberately.
  ultimate: { type: 'sawtooth', freq: 120, sweepTo: 900, duration: 0.6, volume: 0.34 },
  ultimateEnd: { type: 'triangle', freq: 620, sweepTo: 200, duration: 0.35, volume: 0.18 },
  // A ground slam / dash detonation landing (ultimate:shockwave).
  shockwave: { type: 'noise', duration: 0.3, volume: 0.24, filterFreq: 620 },

  // --- champion signatures ---
  // Iris's crit: a sharp glassy ping on top of the normal hit cue.
  crit: { type: 'square', freq: 1500, sweepTo: 520, duration: 0.08, volume: 0.16, pitchJitter: 0.08, minInterval: 0.05 },
  // Rook's Aegis plate taking a hit instead of his HP — a dull metal clang.
  plateBreak: { type: 'noise', duration: 0.16, volume: 0.24, filterFreq: 1800 },
  plateRestore: { type: 'sine', freq: 420, sweepTo: 660, duration: 0.14, volume: 0.11 },
  // Vess's Cadence topping out.
  cadenceMax: { type: 'sine', freq: 900, sweepTo: 1350, duration: 0.14, volume: 0.13 },
  // Aria's Refrain carrying to the next enemy — a short rising chime, and
  // rate-limited because one kill in a packed wave can fire several at once.
  refrainCarry: { type: 'sine', freq: 660, sweepTo: 1180, duration: 0.12, volume: 0.05, minInterval: 0.06 },

  // --- dealing damage ---
  hit: { type: 'square', freq: 1000, sweepTo: 180, duration: 0.07, volume: 0.18, pitchJitter: 0.1, minInterval: 0.04 },
  minionHit: { type: 'square', freq: 760, sweepTo: 300, duration: 0.045, volume: 0.07, pitchJitter: 0.12, minInterval: 0.06 },
  enemyDeath: { type: 'noise', duration: 0.12, volume: 0.12, filterFreq: 1400 },
  minibossDown: { type: 'noise', duration: 0.4, volume: 0.24, filterFreq: 900 },
  explosion: { type: 'noise', duration: 0.6, volume: 0.3, filterFreq: 800 },
  // Nova Pulse's own AoE burst (see JuiceDirector's weapon:aoe-burst) — a
  // low, boomy thump distinct from the sharp per-tick `hit` cue.
  aoeBurst: { type: 'noise', duration: 0.22, volume: 0.18, filterFreq: 550 },

  // --- taking damage ---
  playerHit: { type: 'sawtooth', freq: 220, sweepTo: 80, duration: 0.18, volume: 0.3 },

  // --- boss beats ---
  warning: { type: 'triangle', freq: 180, sweepTo: 120, duration: 0.22, volume: 0.2 },
  phaseChange: { type: 'sine', freq: 150, sweepTo: 600, duration: 0.5, volume: 0.3 },
  vulnOpen: { type: 'triangle', freq: 420, sweepTo: 1040, duration: 0.22, volume: 0.22 },
  vulnClose: { type: 'triangle', freq: 900, sweepTo: 420, duration: 0.16, volume: 0.14 },
  desperation: { type: 'sawtooth', freq: 300, sweepTo: 90, duration: 0.7, volume: 0.32 },
  desperationEnd: { type: 'sine', freq: 392, sweepTo: 784, duration: 0.5, volume: 0.26 },
  // Spell-card capture (BULLET-HELL.md §D1) — a clean rising bell, pitched
  // well above the warning/desperation register so it reads as an award
  // arriving rather than as a threat, even while the next card is telegraphing
  // underneath it. The desperation capture gets the same cue an octave down
  // and longer, since it closes the fight.
  capture: { type: 'triangle', freq: 880, sweepTo: 1760, duration: 0.3, volume: 0.24 },
  captureDesperation: { type: 'triangle', freq: 440, sweepTo: 1320, duration: 0.55, volume: 0.3 },
  // Bullet cancel (BULLET-HELL.md §D2) — a downward wash, the opposite shape
  // to `capture`'s rising bell: this is a curtain being swept off the screen,
  // not an award arriving. Deliberately soft, because it always plays under
  // either `phaseChange` or `ultimate`, never alone.
  bulletCancel: { type: 'sine', freq: 1400, sweepTo: 300, duration: 0.35, volume: 0.16 },
  // Deathbomb (BULLET-HELL.md §D3) — the window opening is a hard, bright
  // alarm tick, deliberately the most attention-grabbing short cue in the
  // table: it has a sixth of a second to make the player's thumb move. The
  // bomb landing answers it an octave up and much longer, a release rather
  // than an alarm, so "I made it" and "I didn't" never sound alike.
  deathbombWindow: { type: 'square', freq: 1760, sweepTo: 1320, duration: 0.09, volume: 0.3 },
  deathbomb: { type: 'triangle', freq: 660, sweepTo: 2640, duration: 0.5, volume: 0.32 },
  // Polarity absorption (BULLET-HELL.md §D7). `absorb` is a bullet being *taken
  // in*, so it sweeps DOWN and inward where `graze` sweeps up and away — the
  // two fire within a second of each other all through a Twin Voice ring and
  // have to be distinguishable by shape alone. Leans on minInterval as hard as
  // `graze` for the same reason: a whole matched half-ring can land at once.
  absorb: { type: 'sine', freq: 1200, sweepTo: 780, duration: 0.05, volume: 0.06, pitchJitter: 0.12, minInterval: 0.08 },
  // The release answers the absorb stream: same family, an octave lower at the
  // start and rising instead of falling, long enough to read as an event.
  polarityRelease: { type: 'triangle', freq: 600, sweepTo: 1800, duration: 0.32, volume: 0.26 },

  // --- waves / pickups / progression ---
  waveStart: { type: 'triangle', freq: 240, sweepTo: 360, duration: 0.25, volume: 0.16 },
  waveClear: { type: 'sine', freq: 520, sweepTo: 1180, duration: 0.4, volume: 0.24 },
  podSpawn: { type: 'sine', freq: 980, sweepTo: 1400, duration: 0.2, volume: 0.1 },
  pickup: { type: 'sine', freq: 620, sweepTo: 980, duration: 0.18, volume: 0.18 },
  // Resonant Pillars: a glassy tick per crack threshold, a real break on
  // shatter — crack rides minInterval since a dense volley can cross both
  // thresholds within a frame or two.
  pillarCrack: { type: 'triangle', freq: 1200, sweepTo: 900, duration: 0.08, volume: 0.12, pitchJitter: 0.08, minInterval: 0.1 },
  pillarShatter: { type: 'noise', duration: 0.3, volume: 0.2, filterFreq: 2200 },
  // Echo Duel: a darker arrival shimmer for the rift, and a descending
  // sweep when the duel is accepted — the ghost's death itself reuses the
  // normal miniboss cues for free (it IS a miniboss).
  echoRift: { type: 'triangle', freq: 700, sweepTo: 350, duration: 0.25, volume: 0.12 },
  echoAccept: { type: 'sawtooth', freq: 500, sweepTo: 120, duration: 0.5, volume: 0.22 },
  levelup: { type: 'triangle', freq: 500, sweepTo: 1000, duration: 0.3, volume: 0.3 },

  // --- gauntlet interloper signatures (2026-07 bosses) ---
  // The Iron Cadence's conducted beat — a dry woodblock-ish tick. Fires on
  // every downbeat of Staccato Volley / Conductor's Baton, so it leans on
  // minInterval like graze does; it's a metronome, not an alarm.
  cadenceTick: { type: 'square', freq: 1180, sweepTo: 900, duration: 0.035, volume: 0.09, minInterval: 0.15 },
  // The Grand Pause releasing — the whole frozen screen surging at once.
  cadenceSurge: { type: 'sawtooth', freq: 190, sweepTo: 950, duration: 0.32, volume: 0.26 },
  // The Famished Court swallowing a courtier — a low, wet gulp.
  devour: { type: 'sine', freq: 320, sweepTo: 85, duration: 0.28, volume: 0.22, pitchJitter: 0.08 },
  // The Waltz of Daggers flinging its blade ring outward.
  waltzFling: { type: 'noise', duration: 0.22, volume: 0.16, filterFreq: 2600 },
  // A broken Vow erupting at the abandoned seal.
  vowBroken: { type: 'sawtooth', freq: 640, sweepTo: 140, duration: 0.3, volume: 0.24 },

  // --- ascension modifiers ---
  enrage: { type: 'sawtooth', freq: 140, sweepTo: 420, duration: 0.4, volume: 0.26 },
  arenaPulse: { type: 'triangle', freq: 200, sweepTo: 90, duration: 0.3, volume: 0.2 },

  // --- run bookends / UI ---
  victory: { type: 'sine', freq: 440, sweepTo: 880, duration: 0.8, volume: 0.35 },
  defeat: { type: 'sawtooth', freq: 220, sweepTo: 60, duration: 0.9, volume: 0.35 },
  click: { type: 'square', freq: 600, duration: 0.04, volume: 0.15 },
};
